<template>
  <div class="login-shell">
    <section class="login-introduction">
      <button type="button" class="login-brand" @click="home">
        <span class="login-brand__mark"><v-icon color="white">school</v-icon></span>
        <span class="login-brand__name">taleem<span>.app</span></span>
      </button>
      <div class="login-introduction__content">
        <div class="login-eyebrow">Masjid administration</div>
        <h1>Check your inbox.</h1>
        <p>Your reset link will help you get back to your Taleem workspace.</p>
        <div class="login-feature">
          <v-icon>check_circle</v-icon>
          <span>Simple, secure, and built for your community</span>
        </div>
      </div>
    </section>

    <v-card class="login-card" elevation="0">
      <div class="login-card__heading">
        <div class="login-card__icon"><v-icon>mark_email_read</v-icon></div>
        <div>
          <div class="login-eyebrow">Account recovery</div>
          <h2>Check your email</h2>
          <p>The reset email request was accepted for your admin account.</p>
        </div>
      </div>

      <v-card-text class="login-card__body">
        <div class="auth-recovery-email" role="status">
          <v-icon color="primary">email</v-icon>
          <div>
            <span>Admin email</span>
            <strong>{{ recovery.email }}</strong>
            <span class="auth-recovery-email__code">Organization code</span>
            <strong>{{ recovery.code }}</strong>
          </div>
        </div>
        <p class="auth-recovery-help">Delivery can take a few minutes. Check your spam folder if you don't see it. The link is valid for 100 days and works once.</p>
        <v-btn
          :loading="loading"
          :disabled="secondsRemaining > 0"
          class="login-submit"
          color="primary"
          depressed
          block
          large
          @click="resend"
        >
          {{ secondsRemaining > 0 ? `Resend available in ${secondsRemaining}s` : 'Resend email' }}
          <v-icon v-if="secondsRemaining === 0" right>arrow_forward</v-icon>
        </v-btn>
        <p v-if="resent" class="auth-status" role="status">Another reset email request was accepted. Check your inbox and spam folder.</p>
        <p v-if="requestError" class="auth-status auth-status--error" role="alert">{{ requestError }}</p>
      </v-card-text>

      <div class="login-card__signup">
        <button type="button" @click="$router.push('/forgot')">Use another account</button>
        <span class="auth-recovery-separator">or</span>
        <button type="button" @click="$router.push('/login')">Back to sign in</button>
      </div>
    </v-card>
  </div>
</template>

<script>
import authService from '../service'

export default {
  name: 'authentication-recovery-email-sent',
  data() {
    return {
      recovery: { code: '', email: '' },
      cooldownUntil: 0,
      now: Date.now(),
      timer: null,
      loading: false,
      resent: false,
      requestError: '',
    }
  },
  computed: {
    secondsRemaining() {
      return Math.max(0, Math.ceil((this.cooldownUntil - this.now) / 1000))
    },
  },
  created() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}adminRecovery`) || 'null')
      if (!saved || !saved.code || !saved.email) throw new Error('Missing recovery request')
      this.recovery = saved
      this.cooldownUntil = saved.sentAt + saved.retryAfterSeconds * 1000
    } catch (error) {
      this.$router.replace('/forgot')
    }
  },
  mounted() {
    this.timer = setInterval(() => { this.now = Date.now() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    home() { window.open('https://taleem.com', '_self') },
    async resend() {
      if (this.loading || this.secondsRemaining > 0) return
      this.loading = true
      this.resent = false
      this.requestError = ''
      try {
        const response = await authService.forgotPassword(this.recovery)
        this.recovery.email = response.data.email
        this.cooldownUntil = Date.now() + response.data.retryAfterSeconds * 1000
        this.now = Date.now()
        sessionStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}adminRecovery`, JSON.stringify({
          code: this.recovery.code,
          email: this.recovery.email,
          sentAt: this.now,
          retryAfterSeconds: response.data.retryAfterSeconds,
        }))
        this.resent = true
      } catch (error) {
        if (error && error.status === 429 && error.data && error.data.retryAfterSeconds) {
          this.cooldownUntil = Date.now() + error.data.retryAfterSeconds * 1000
          this.now = Date.now()
        }
        this.requestError = (error && error.data && error.data.message) || 'Unable to resend the email. Please try again.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
